/**
 * Verbindung zum Lehrroboter ueber Web Serial.
 *
 * Setzt dieselbe Schnittstelle um wie die micro:bit-Verbindung des Editors
 * (`MicrobitUSBConnection`), damit Oberflaeche, Konsole und Projektaktionen
 * unveraendert bleiben. Der Simulator des Editors geht denselben Weg.
 *
 * Wichtiger Unterschied zum micro:bit
 * -----------------------------------
 * Beim micro:bit erzeugt `flash` eine HEX-Datei mit Firmware und Programm und
 * schreibt sie ueber WebUSB. Beim Pico 2 W sind das zwei getrennte Vorgaenge
 * (W04):
 *
 * `flash`
 *     uebertraegt **nur** die Python-Dateien des Projekts und startet sie.
 *     Das ist der haeufige Fall im Unterricht.
 * Firmwareinstallation
 *     laeuft ueber eine UF2-Datei im BOOTSEL-Modus, ausserhalb dieser Klasse.
 *     Selten, ausdruecklich, und ohne Verwechslungsgefahr mit dem Upload.
 *
 * Browserunterstuetzung
 * ---------------------
 * Web Serial gibt es in Chrome und Edge. Firefox und Safari unterstuetzen es
 * nicht; dort meldet `checkAvailability()` "unsupported", und die Oberflaeche
 * bietet den Download der Dateien an (W06).
 */

import {
  ConnectionStatus,
  ConnectionStatusChange,
  DeviceError,
  type BackgroundErrorData,
  type BoardVersion,
  type ConnectionAvailabilityStatus,
  type ConnectOptions,
  type FlashOptions,
} from "@microbit/microbit-connection";
import type { MicrobitUSBConnection, SerialData } from "@microbit/microbit-connection/usb";
import { Logging } from "../../logging/logging";
import { SimpleEventTarget } from "../simple-event-target";
import {
  formatCommand,
  HELLO_LINE,
  TelemetryReader,
  type RemoteControl,
  type RemoteValue,
  type TelemetrySample,
} from "../../telemetry/parse";
import { checkCompatibility, type CompatibilityResult } from "./compatibility";
import {
  LAUNCHER_PATH,
  LAUNCHER_SOURCE,
  MANIFEST_PATH,
} from "./launcher";
import { CTRL_B, CTRL_C, CTRL_D, RawRepl, SerialReader } from "./raw-repl";
import { asDeviceError } from "./errors";
import { fehlerDaten, protokoll } from "./protokoll";
import type { Transport, TransportKind } from "./transport";
import { RASPBERRY_PI_VENDOR_ID, SerialTransport } from "./serial-transport";
import { BluetoothTransport } from "./bluetooth-transport";

export { RASPBERRY_PI_VENDOR_ID };

/** Ablage der Schuelerprojekte auf dem Board. */
const PROJECT_ROOT = "/robot_projects";

/** Name der Datei, die das Startprogramm ausfuehrt. */
const MAIN_FILE = "main.py";

/** Beschreibt das verbundene Board (W03). */
export interface RobotDeviceInfo {
  board: string;
  micropython: string;
  firmware: string;
  api: string;
  profile: string;
  capabilities: string[];
  uniqueId: string;
}

export interface PicoEventMap {
  status: ConnectionStatusChange;
  backgrounderror: BackgroundErrorData;
  beforerequestdevice: void;
  afterrequestdevice: void;
  flash: void;
  serialdata: SerialData;
  serialreset: void;
  /** Das Geraeteprofil wurde nach dem Verbinden gelesen. */
  deviceinfo: { info: RobotDeviceInfo | undefined };
  telemetry: { sample: TelemetrySample };
  remotecontrols: { controls: RemoteControl[] };
}

/**
 * Groesste Zeile, die der Roboter im Rueckkanal noch annimmt.
 *
 * Muss zu ``MAX_LINE`` in ``python/robot/_remote.py`` passen. Was darueber
 * hinausgeht, wird dort verworfen, ohne dass jemand es erfaehrt.
 */
const MAX_LINE = 512;

/**
 * So lange wartet das Verbinden auf die Antwort eines laufenden Programms.
 *
 * Ein Programm antwortet in ``robot.sleep()``. Am Geraet dauerte ein Stopp
 * ueber Funk hin und zurueck 97 bis 212 ms (docs/messungen/2026-09-14-e2-am-
 * stueck.md); die Antwort geht denselben Weg. Laeuft nichts, verlaengert das
 * Verbinden sich um diese Zeit.
 */
const HALLO_FRIST_MS = 600;

export class PicoDeviceConnection
  extends SimpleEventTarget<PicoEventMap>
  implements MicrobitUSBConnection
{
  readonly type = "usb" as const;
  status: ConnectionStatus = ConnectionStatus.NoAuthorizedDevice;

  /**
   * Der Weg zum Roboter. Ueber Kabel oder ueber Funk - darueber weiss von
   * hier an nichts mehr Bescheid, weil der Roboter auf beiden Wegen dieselbe
   * Konsole anbietet (python/robot/_ble.py).
   */
  private transport: Transport = new SerialTransport();
  private reader: SerialReader | undefined;
  private repl: RawRepl | undefined;
  private deviceInfo: RobotDeviceInfo | undefined;
  private readonly telemetryReader = new TelemetryReader();
  private deviceId: number | undefined;
  /** Waehrend einer Uebertragung hat der Upload die Verbindung allein (W11). */
  private exclusive = false;
  /** Wartet gerade jemand auf die Antwort eines laufenden Programms? */
  private infoWartend: ((roh: unknown) => void) | undefined;
  /**
   * Laeuft auf dem Roboter gerade ein Programm, das Bedienwerte liest?
   *
   * Nur dann gehen Rueckkanalbefehle hinaus. Am 14.09.2026 im Protokoll: das
   * Programm brach mit einem ValueError ab, die Oberflaeche schickte weiter
   * Joystickwerte - und die REPL gab jeden Befehl Zeichen fuer Zeichen als
   * eigene Funkmeldung zurueck, samt SyntaxError. Hunderte Meldungen je
   * Sekunde; die Funkverbindung brach zusammen, einmal kam sie nicht wieder.
   * Die eigentliche Fehlermeldung ging in der Flut unter.
   */
  private programmLaeuft = false;

  constructor(private readonly logging: Logging) {
    super();
  }

  // ----------------------------------------------------------------------
  // Verfuegbarkeit und Verbindungsaufbau
  // ----------------------------------------------------------------------

  async initialize(): Promise<void> {
    if (!this.transport.isSupported()) {
      this.setStatus(ConnectionStatus.NoAuthorizedDevice);
      return;
    }
    this.setStatus(
      (await this.transport.hasAuthorizedDevice())
        ? ConnectionStatus.Disconnected
        : ConnectionStatus.NoAuthorizedDevice
    );
  }

  async checkAvailability(): Promise<ConnectionAvailabilityStatus> {
    return this.transport.isSupported() ? "available" : "unsupported";
  }

  /** Worueber gerade verbunden ist oder verbunden wuerde. */
  getTransportKind(): TransportKind {
    return this.transport.kind;
  }

  /** Kann dieser Browser ueber Funk? */
  isBluetoothSupported(): boolean {
    return new BluetoothTransport().isSupported();
  }

  /**
   * Waehlt den Weg fuer die naechste Verbindung.
   *
   * Getrennt vom Verbinden, damit die Oberflaeche vorher fragen kann, ob
   * dieser Browser den Weg ueberhaupt kann - ``checkAvailability`` antwortet
   * immer fuer den gerade gewaehlten.
   *
   * Ein Wechsel trennt eine bestehende Verbindung. Zwei Wege gleichzeitig
   * waeren zwei Konsolen auf demselben Roboter: die Telemetrie ginge doppelt
   * hinaus, und der Regelkern zahlte sie zweimal.
   */
  async setTransport(kind: TransportKind): Promise<void> {
    if (kind === this.transport.kind) {
      return;
    }
    await this.disconnect();
    this.transport.dispose();
    this.transport =
      kind === "bluetooth" ? new BluetoothTransport() : new SerialTransport();
    await this.initialize();
  }

  async connect(options: ConnectOptions = {}): Promise<void> {
    if (this.status === ConnectionStatus.Connected) {
      return;
    }
    this.setStatus(ConnectionStatus.Connecting);
    options.progress?.("FindingDevice");
    const beginn = performance.now();
    protokoll.ereignis("verbindung", "verbinden beginnt", { weg: this.transport.kind });
    try {
      this.startReading();
      await this.transport.open({
        onData: (data) => this.reader?.push(data),
        onLost: () => this.handleLost(),
        // Kein Rueckfall auf die Raw-REPL wie beim ersten Verbinden: ein
        // Abriss mitten im Betrieb heisst nicht, dass nichts laeuft, und ein
        // Strg-C haelte genau das Programm an, das gerade gestartet wurde.
        onReconnected: () => {
          protokoll.ereignis("verbindung", "nach wiederverbinden erneut gefragt", {
            status: this.status,
            exklusiv: this.exclusive,
          });
          void this.writeRaw(HELLO_LINE + "\r\n").catch((error) =>
            protokoll.ereignis("verbindung", "erneute frage fehlgeschlagen", fehlerDaten(error))
          );
        },
        beforeRequestDevice: () => this.dispatchEvent("beforerequestdevice"),
        afterRequestDevice: () => this.dispatchEvent("afterrequestdevice"),
      });
      options.progress?.("Connecting");
      this.repl = new RawRepl(this.reader!, (text) => this.writeRaw(text));
      // Erst fragen, dann erst die Raw-REPL. Die Raw-REPL braucht Strg-C und
      // beendet damit jedes laufende Programm - wer den Roboter verband, sah
      // deshalb nie Plots oder Bedienelemente, bis er neu uebertrug. Ein
      // laufendes Programm antwortet selbst; nur wenn nichts antwortet, ist
      // der Abbruch harmlos, weil nichts laeuft.
      protokoll.ereignis("verbindung", "leitung offen", {
        seit_beginn_ms: Math.round(performance.now() - beginn),
      });
      const antwort = await this.fragLaufendesProgramm();
      this.deviceInfo =
        antwort !== null ? this.geraetAus(antwort) : await this.readDeviceInfo();
      this.dispatchEvent("deviceinfo", { info: this.deviceInfo });
      this.setStatus(ConnectionStatus.Connected);
      protokoll.ereignis("verbindung", "verbunden", {
        seit_beginn_ms: Math.round(performance.now() - beginn),
        quelle: antwort !== null ? "laufendes programm" : "raw-repl",
        firmware: this.deviceInfo?.firmware,
        api: this.deviceInfo?.api,
        board: this.deviceInfo?.uniqueId,
        // Ohne Geraeteinformation verweigert uploadProject jede Uebertragung
        // mit "keine Roboterfirmware" - auch wenn die Firmware da ist und nur
        // das Lesen scheiterte.
        vertraeglichkeit: this.getCompatibility().level,
      });
    } catch (error) {
      protokoll.ereignis("verbindung", "verbinden fehlgeschlagen", {
        seit_beginn_ms: Math.round(performance.now() - beginn),
        ...fehlerDaten(error),
      });
      await this.closePort();
      this.setStatus(ConnectionStatus.Disconnected);
      throw asDeviceError(error);
    }
  }

  async disconnect(): Promise<void> {
    protokoll.ereignis("verbindung", "trennen (vom nutzer)");
    this.programmLaeuft = false;
    await this.closePort();
    this.setStatus(ConnectionStatus.Disconnected);
  }

  async clearDevice(): Promise<void> {
    await this.closePort();
    this.deviceInfo = undefined;
    this.deviceId = undefined;
    await this.transport.forget();
    this.setStatus(ConnectionStatus.NoAuthorizedDevice);
  }

  dispose(): void {
    this.transport.dispose();
    void this.closePort();
  }

  private handleLost(): void {
    protokoll.ereignis("verbindung", "verlust gemeldet", { status: this.status });
    this.programmLaeuft = false;
    if (this.status === ConnectionStatus.Connected) {
      this.logging.log("Verbindung zum Roboter verloren.");
      void this.closePort();
      this.setStatus(ConnectionStatus.Disconnected);
    }
  }

  // ----------------------------------------------------------------------
  // Lesen und Schreiben
  // ----------------------------------------------------------------------

  private startReading(): void {
    const reader = new SerialReader((text) => {
      if (this.exclusive) {
        return;
      }
      // Telemetriezeilen aus dem Konsolentext herausnehmen (W11). Ohne diese
      // Trennung wuerde die Messwertausgabe die Ausgaben der Studierenden
      // ueberschwemmen und waere in der Konsole nicht zu gebrauchen.
      const result = this.telemetryReader.push(text);
      if (result.text) {
        this.dispatchEvent("serialdata", { data: result.text });
      }
      for (const sample of result.samples) {
        this.dispatchEvent("telemetry", { sample });
      }
      if (result.controls || result.samples.length > 0 || result.info !== undefined) {
        if (!this.programmLaeuft) {
          protokoll.ereignis("verbindung", "programm laeuft");
        }
        this.programmLaeuft = true;
      }
      if (result.controls) {
        this.dispatchEvent("remotecontrols", { controls: result.controls });
      }
      if (result.info !== undefined) {
        this.infoWartend?.(result.info);
      }
      if (result.prompt || result.reboot) {
        this.programmBeendet(result.prompt ? "eingabeaufforderung" : "soft-reboot");
      }
    });
    this.reader = reader;
  }

  /**
   * Das Programm auf dem Roboter ist zu Ende: keine Bedienwerte mehr senden,
   * und die Bedienelemente verschwinden aus der Oberflaeche. Ein neues
   * Programm meldet seine eigenen an.
   */
  private programmBeendet(grund: string): void {
    if (this.programmLaeuft) {
      protokoll.ereignis("verbindung", "programm beendet", { grund });
    }
    this.programmLaeuft = false;
    this.dispatchEvent("remotecontrols", { controls: [] });
  }

  private async writeRaw(text: string): Promise<void> {
    await this.transport.write(text);
  }

  /**
   * Schickt Werte an das laufende Programm (Rueckkanal).
   *
   * Das Programm liest sie in `robot.sleep()`. Waehrend einer Uebertragung
   * wird nichts gesendet: dort gehoert die Leitung dem Uploadprotokoll, und
   * ein dazwischengeschobener Befehl wuerde es stoeren.
   */
  async sendRemote(values: Record<string, RemoteValue>): Promise<void> {
    if (
      this.exclusive ||
      !this.programmLaeuft ||
      this.status !== ConnectionStatus.Connected
    ) {
      // Ohne laufendes Programm landete der Befehl in der REPL, und die gaebe
      // ihn Zeichen fuer Zeichen zurueck. Siehe programmLaeuft.
      return;
    }
    const befehl = formatCommand(values);
    if (befehl.length >= MAX_LINE) {
      // Der Roboter verwirft eine zu lange Zeile **stillschweigend**
      // (``python/robot/_remote.py``, ``MAX_LINE``). Seit ein Lebenszeichen den
      // vollstaendigen Zustand traegt, ist das ueberhaupt erreichbar: bei rund
      // 22 Joysticks oder 25 Schiebereglern. Das kommt in keiner Uebung vor -
      // aber wenn doch, soll es nicht dasselbe stille Verschwinden sein, das
      // uns den ganzen 13.09. gekostet hat.
      console.warn(
        `Zu viele Bedienelemente: ${befehl.length} Zeichen, der Roboter nimmt ` +
          `hoechstens ${MAX_LINE - 1}. Die Werte kommen nicht an.`
      );
    }
    await this.writeRaw(befehl + "\r\n");
  }

  async serialWrite(data: string): Promise<void> {
    await this.writeRaw(data);
  }

  private async closePort(): Promise<void> {
    this.exclusive = false;
    try {
      await this.transport.close();
    } finally {
      this.reader?.close();
      this.reader = undefined;
      this.repl = undefined;
    }
  }

  // ----------------------------------------------------------------------
  // Geraeteprofil (W03)
  // ----------------------------------------------------------------------

  /**
   * Fragt ein laufendes Programm nach der Geraeteinformation.
   *
   * @returns die rohe Antwort, oder ``null``, wenn innerhalb der Frist nichts
   *   kam - dann laeuft nichts, die Firmware kennt die Frage nicht, oder das
   *   Programm ruft nie ``robot.sleep()`` auf.
   */
  private async fragLaufendesProgramm(): Promise<unknown> {
    const beginn = performance.now();
    protokoll.ereignis("verbindung", "frage laufendes programm");
    return new Promise<unknown>((resolve) => {
      const timer = setTimeout(() => {
        this.infoWartend = undefined;
        protokoll.ereignis("verbindung", "keine antwort, weiter ueber raw-repl", {
          frist_ms: HALLO_FRIST_MS,
        });
        resolve(null);
      }, HALLO_FRIST_MS);
      this.infoWartend = (roh) => {
        clearTimeout(timer);
        this.infoWartend = undefined;
        protokoll.ereignis("verbindung", "programm antwortet", {
          nach_ms: Math.round(performance.now() - beginn),
        });
        resolve(roh);
      };
      this.writeRaw(HELLO_LINE + "\r\n").catch((error) => {
        clearTimeout(timer);
        this.infoWartend = undefined;
        protokoll.ereignis("verbindung", "frage nicht gesendet", fehlerDaten(error));
        resolve(null);
      });
    });
  }

  /**
   * Wertet die Geraeteinformation aus - gleich, ob sie aus der Raw-REPL kam
   * oder als Antwort eines laufenden Programms. Beide haben denselben Aufbau.
   */
  private geraetAus(roh: unknown): RobotDeviceInfo | undefined {
    const parsed = roh as {
      info: { board: string; firmware: string; api: string; profile: string } | null;
      capabilities: string[];
      unique_id: string;
      micropython: string;
    };
    this.deviceId = parseInt(parsed.unique_id.slice(0, 8), 16);
    if (!parsed.info) {
      // Ein Board mit MicroPython, aber ohne Roboterfirmware. Kein Fehler:
      // die Oberflaeche soll das erklaeren koennen, statt nur zu scheitern.
      return undefined;
    }
    return {
      board: parsed.info.board,
      micropython: parsed.micropython,
      firmware: parsed.info.firmware,
      api: parsed.info.api,
      profile: parsed.info.profile,
      capabilities: parsed.capabilities,
      uniqueId: parsed.unique_id,
    };
  }

  private async readDeviceInfo(): Promise<RobotDeviceInfo | undefined> {
    const repl = this.repl!;
    this.exclusive = true;
    try {
      const output = await repl.execChecked(
        [
          "import sys, json, machine",
          "try:",
          "    import robot",
          "    _i = robot.info()",
          "    _c = list(robot.capabilities())",
          "except ImportError:",
          "    _i = None",
          "    _c = []",
          "print(json.dumps({",
          "    'info': _i,",
          "    'capabilities': _c,",
          "    'unique_id': machine.unique_id().hex(),",
          "    'micropython': '.'.join(str(p) for p in sys.implementation.version[:3]),",
          "}))",
          "",
        ].join("\n")
      );
      const geraet = this.geraetAus(JSON.parse(output.trim()));
      protokoll.ereignis("verbindung", "geraeteinformation gelesen", {
        roboterfirmware: geraet !== undefined,
      });
      return geraet;
    } catch (error) {
      protokoll.ereignis("verbindung", "geraeteinformation nicht lesbar", fehlerDaten(error));
      this.logging.error(error);
      return undefined;
    } finally {
      this.exclusive = false;
      await repl.exitRaw().catch(() => undefined);
    }
  }

  /** Das zuletzt gelesene Geraeteprofil, oder `undefined` ohne Roboterfirmware. */
  getRobotInfo(): RobotDeviceInfo | undefined {
    return this.deviceInfo;
  }

  /** Wie gut die Firmware zu dieser Oberflaeche passt (W03). */
  getCompatibility(): CompatibilityResult {
    return checkCompatibility(this.deviceInfo);
  }

  getDeviceId(): number {
    if (this.deviceId === undefined) {
      throw new DeviceError({
        code: "not-connected",
        message: "Es war noch kein Board verbunden.",
      });
    }
    return this.deviceId;
  }

  getBoardVersion(): BoardVersion {
    // Die Schnittstelle kennt nur micro:bit-Versionen, und der Roboter ist
    // keine davon. "V2" ist hier kein Aussage ueber die Hardware, sondern der
    // Wert, mit dem der Editor seinen aktuellen Pfad waehlt. Die tatsaechliche
    // Fassung von Board, Firmware und API steht in getRobotInfo() (W03).
    // Der Simulator des Editors verfaehrt genauso.
    return "V2";
  }

  getDevice() {
    // Web Serial liefert kein USBDevice. Teil der uebernommenen Schnittstelle.
    return undefined;
  }

  setRequestDeviceExclusionFilters(): void {
    // Nur fuer WebUSB sinnvoll; hier ohne Wirkung.
  }

  // ----------------------------------------------------------------------
  // Programm uebertragen, starten und stoppen
  // ----------------------------------------------------------------------

  /**
   * Uebertraegt die Python-Dateien des Projekts und startet das Programm.
   *
   * Uebertragen wird in einen neuen Ordner; erst wenn alle Dateien
   * angekommen sind, wird er zum aktuellen Projekt erklaert. Ein Abbruch
   * mittendrin laesst damit kein halb ersetztes Projekt zurueck (W07).
   */
  async uploadProject(
    files: Record<string, string | Uint8Array>,
    options: FlashOptions = {}
  ): Promise<void> {
    const repl = this.repl;
    if (!repl || this.status !== ConnectionStatus.Connected) {
      throw new DeviceError({
        code: "not-connected",
        message: "Es besteht keine Verbindung zum Board.",
      });
    }
    // Der Abgleich steht vor der Uebertragung, nicht danach: sonst faellt der
    // Konflikt erst als Traceback auf, dessen Ursache im Programm nicht zu
    // sehen ist (W03).
    const compatibility = this.getCompatibility();
    protokoll.ereignis("verbindung", "uebertragen beginnt", {
      dateien: Object.keys(files),
      bytes: Object.values(files).reduce(
        (summe, inhalt) => summe + (typeof inhalt === "string" ? inhalt.length : inhalt.length),
        0
      ),
      vertraeglichkeit: compatibility.level,
    });
    if (compatibility.level === "missing") {
      throw new DeviceError({
        code: "firmware-update-required",
        message:
          "Auf diesem Board ist keine Roboterfirmware installiert. " +
          "Das Modul robot fehlt, deshalb kann kein Programm laufen. " +
          "Bitte zuerst die Firmware im BOOTSEL-Modus installieren.",
      });
    }
    if (compatibility.level === "major") {
      throw new DeviceError({
        code: "firmware-update-required",
        message:
          `Die Firmware auf dem Board bietet die Roboter-API ` +
          `${compatibility.deviceVersion}, diese Oberflaeche erwartet ` +
          `${compatibility.expectedVersion}. Aufrufe koennen fehlen oder ` +
          "anders heissen, und die API-Hilfe passt nicht zum Board. " +
          "Bitte die zu dieser Oberflaeche gehoerende Firmware installieren.",
      });
    }

    this.dispatchEvent("flash");
    this.exclusive = true;
    options.progress?.("FullFlashing", 0);
    const target = `${PROJECT_ROOT}/${randomProjectName()}`;
    const mainFile = MAIN_FILE in files ? MAIN_FILE : Object.keys(files)[0];
    try {
      await repl.interrupt();
      await repl.makeDirectory(PROJECT_ROOT);
      await repl.makeDirectory(target);

      const names = Object.keys(files);
      const steps = names.length + 2;
      for (let index = 0; index < names.length; index += 1) {
        const name = names[index];
        const dateiBeginn = performance.now();
        await repl.writeFile(`${target}/${name}`, files[name]);
        protokoll.ereignis("verbindung", "datei uebertragen", {
          name,
          dauer_ms: Math.round(performance.now() - dateiBeginn),
        });
        options.progress?.("FullFlashing", (index + 1) / steps);
      }

      // Das Startprogramm liegt fest unter /main.py. MicroPython fuehrt es
      // nach jedem Neustart aus; ohne diese Datei passiert nach der
      // Uebertragung nichts.
      await repl.writeFile(LAUNCHER_PATH, LAUNCHER_SOURCE);
      options.progress?.("FullFlashing", (names.length + 1) / steps);

      // Erst jetzt gilt das Projekt als vollstaendig uebertragen.
      await repl.writeFile(
        MANIFEST_PATH,
        JSON.stringify({ schema: 1, path: target, main: mainFile, files: names })
      );
      await this.pruneOldProjects(target);
      options.progress?.("FullFlashing", 1);
    } catch (error) {
      protokoll.ereignis("verbindung", "uebertragen fehlgeschlagen", fehlerDaten(error));
      await repl.removeTree(target).catch(() => undefined);
      throw asDeviceError(error);
    } finally {
      this.exclusive = false;
    }
    protokoll.ereignis("verbindung", "uebertragen fertig, starte");
    await this.run();
  }

  /**
   * Teil der uebernommenen Schnittstelle, hier ohne Verwendung.
   *
   * Beim micro:bit erzeugt `flash` eine HEX-Datei aus Firmware und Programm.
   * Der Pico 2 W kennt diesen Weg nicht: Programme gehen ueber
   * {@link uploadProject}, Firmware ueber eine UF2-Datei im BOOTSEL-Modus
   * (W04). Der Fehler ist absichtlich deutlich, damit ein versehentlicher
   * Aufruf auffaellt statt still nichts zu tun.
   */
  async flash(): Promise<void> {
    throw new DeviceError({
      code: "unsupported",
      message:
        "Der Lehrroboter wird nicht ueber eine HEX-Datei beschrieben. " +
        "Programme werden mit uploadProject uebertragen, Firmware als UF2-Datei " +
        "im BOOTSEL-Modus installiert.",
    });
  }

  /**
   * Startet das uebertragene Projekt.
   *
   * Der Weg ist ein Soft-Reset in der normalen REPL: MicroPython fuehrt danach
   * /main.py aus, also genau dasselbe wie nach dem Einschalten. Das Programm
   * beginnt dadurch mit frischem Speicher und ohne Reste einer vorherigen
   * Sitzung, und die Ausgabe laeuft in die Konsole (W08).
   */
  async run(): Promise<void> {
    protokoll.ereignis("verbindung", "soft-reset und start");
    const repl = this.repl!;
    await repl.exitRaw();
    this.dispatchEvent("serialreset");
    await this.writeRaw(CTRL_C);
    await this.writeRaw(CTRL_D);
  }

  /**
   * Bricht das laufende Programm ab.
   *
   * Ctrl-C beendet den Schuelercode; danach stoppt `robot.stop()` die
   * Ansteuerung auch dann, wenn das Programm kein `try/finally` hatte (W08,
   * G15).
   */
  async stop(): Promise<void> {
    protokoll.ereignis("verbindung", "stopp");
    await this.writeRaw(CTRL_C);
    await this.writeRaw(CTRL_C);
    const repl = this.repl;
    if (!repl) {
      return;
    }
    this.exclusive = true;
    try {
      // execChecked und nicht exec: schlaegt robot.stop() fehl - etwa auf
      // einem Board ohne Roboterfirmware -, dann hat der Abbruch zwar das
      // Programm beendet, die Ansteuerung kann aber weiterlaufen. Das darf
      // nicht still bleiben, sondern muss den Aufrufer erreichen.
      await repl.execChecked("import robot\nrobot.stop()\n", 3000);
    } finally {
      // Der Rueckweg in die normale REPL laeuft in jedem Fall, auch nach
      // einem Fehler: sonst bliebe die Konsole im Raw-Modus stumm.
      this.exclusive = false;
      await repl.exitRaw().catch(() => undefined);
      await this.writeRaw(CTRL_B).catch(() => undefined);
    }
  }

  /**
   * Startet das zuletzt uebertragene Programm, ohne neu zu uebertragen.
   *
   * Vorher wird nachgesehen, ob es eines gibt. Ohne Projekt meldet der
   * Starter das zwar in der Konsole, aber wer auf Start drueckt, schaut nicht
   * unbedingt dorthin.
   *
   * @returns ``false``, wenn kein vollstaendig uebertragenes Projekt da ist.
   */
  async start(): Promise<boolean> {
    protokoll.ereignis("verbindung", "start gedrueckt");
    const repl = this.repl;
    if (!repl) {
      return false;
    }
    this.exclusive = true;
    let vorhanden: boolean;
    try {
      const ausgabe = await repl.execChecked(
        [
          "import os",
          "try:",
          `    os.stat(${JSON.stringify(MANIFEST_PATH)})`,
          `    os.stat(${JSON.stringify(LAUNCHER_PATH)})`,
          "    print('JA')",
          "except OSError:",
          "    print('NEIN')",
          "",
        ].join("\n"),
        3000
      );
      vorhanden = ausgabe.trim() === "JA";
    } finally {
      this.exclusive = false;
    }
    if (!vorhanden) {
      await repl.exitRaw().catch(() => undefined);
      return false;
    }
    await this.run();
    return true;
  }

  async softwareReset(): Promise<void> {
    await this.writeRaw(CTRL_C);
    await this.writeRaw(CTRL_D);
    this.dispatchEvent("serialreset");
  }

  /** Loescht alle Projektordner ausser dem aktuellen. */
  private async pruneOldProjects(keep: string): Promise<void> {
    const repl = this.repl!;
    await repl.execChecked(
      [
        "import os",
        `_keep = ${JSON.stringify(keep.slice(PROJECT_ROOT.length + 1))}`,
        "def _rm(p):",
        "    try:",
        "        for e in os.listdir(p):",
        "            _rm(p + '/' + e)",
        "        os.rmdir(p)",
        "    except OSError:",
        "        try:",
        "            os.remove(p)",
        "        except OSError:",
        "            pass",
        `for _e in os.listdir(${JSON.stringify(PROJECT_ROOT)}):`,
        "    if _e != _keep:",
        `        _rm(${JSON.stringify(PROJECT_ROOT)} + '/' + _e)`,
        "del _rm, _keep",
        "",
      ].join("\n")
    );
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) {
      return;
    }
    const previousStatus = this.status;
    this.status = status;
    protokoll.ereignis("verbindung", "status", { von: previousStatus, nach: status });
    this.dispatchEvent("status", { status, previousStatus });
  }
}

function randomProjectName(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}
